import { NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import ChatWidget from "./ChatWidget";

const PRODUCTS = [
  {
    logo: "/quantum.png",
    name: "Quantum Language",
    sub: "Our own programming language",
    url: "https://quantum.quantumlogicslimited.com/",
  },
  {
    logo: "/quantummeet.png",
    name: "QuantumMeet",
    sub: "Enterprise video conferencing",
    url: "https://meet.quantumlogicslimited.com/",
  },
  {
    logo: "/docssyncer.png",
    name: "DocsSyncer",
    sub: "AI-powered documentation sync",
    url: "https://docssyncer.quantumlogicslimited.com/",
  },
];

/* Tiny inline Discord icon used in the nav link */
function DiscordNavIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="currentColor"
      style={{ marginLeft: "4px", verticalAlign: "middle", opacity: 0.75 }}
    >
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}

export default function Navbar({ currentUser, setShowAuth, logout }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!productsOpen) return;
    const close = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setProductsOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [productsOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => {
      if (!e.target.closest(".nav")) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  const closeAll = () => {
    setMenuOpen(false);
    setProductsOpen(false);
  };

  const dashboardPath =
    currentUser?.role === "admin"
      ? "/admin-panel"
      : currentUser?.role === "interviewer"
        ? "/interviewer-panel"
        : "/dashboard";

  const showChat = currentUser && currentUser.role === "user";

  return (
    <>
      <nav className={`nav${scrolled ? " nav--scrolled" : ""}`}>
        {/* Logo */}
        <div
          className="nav-logo"
          onClick={() => {
            navigate("/");
            closeAll();
          }}
        >
          <img
            src="/logo.png"
            alt="Quantum Logics"
            className="nav-logo-img"
            width="27"
            height="27"
          />
          <span className="nav-logo-text">Quantum Logics</span>
        </div>

        {/* Desktop links */}
        <div className="nav-links">
          <NavLink
            to="/"
            end
            className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}
          >
            Home
          </NavLink>

          <div className="nav-dropdown-wrap" ref={dropdownRef}>
            <button
              className={`nav-dropdown-btn${productsOpen ? " open" : ""}`}
              onClick={() => setProductsOpen((o) => !o)}
            >
              Products
              <svg
                className={`nav-chevron${productsOpen ? " open" : ""}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {productsOpen && (
              <div className="nav-dropdown">
                <div className="nav-dropdown-label">Our Ecosystem</div>
                {PRODUCTS.map((p, i) => (
                  <a
                    key={i}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="nav-dropdown-item"
                    onClick={() => setProductsOpen(false)}
                  >
                    <div className="nav-dropdown-icon">
                      <img src={p.logo} alt={p.name} width="34" height="34" />
                    </div>
                    <div className="nav-dropdown-text">
                      <span className="nav-dropdown-name">{p.name}</span>
                      <span className="nav-dropdown-sub">{p.sub}</span>
                    </div>
                    <svg
                      className="nav-dropdown-arrow"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="7" y1="17" x2="17" y2="7" />
                      <polyline points="7 7 17 7 17 17" />
                    </svg>
                  </a>
                ))}
              </div>
            )}
          </div>

          <NavLink
            to="/jobs"
            className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}
          >
            Careers
          </NavLink>

          {/* <NavLink
            to="/training"
            className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}
          >
            Training
          </NavLink> */}

          {/* ✅ NEW: Discord community page link */}
          <NavLink
            to="/discord"
            className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}
          >
            Discord
            <DiscordNavIcon />
          </NavLink>

          {currentUser && (
            <NavLink
              to={dashboardPath}
              className={({ isActive }) =>
                `nav-link${isActive ? " active" : ""}`
              }
            >
              Dashboard
            </NavLink>
          )}
        </div>

        {/* Desktop right */}
        <div className="nav-right">
          {!currentUser ? (
            <>
              <button
                className="nav-link"
                style={{ background: "none", border: "none" }}
                onClick={() => setShowAuth("login")}
              >
                Sign In
              </button>
              <button
                className="nav-cta"
                onClick={() => setShowAuth("register")}
              >
                Get Started
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </>
          ) : (
            <>
              {showChat && <ChatWidget user={currentUser} asNavButton={true} />}
              <div className="nav-divider" />
              <button
                className="nav-link"
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--text-3)",
                }}
                onClick={logout}
              >
                Sign Out
              </button>
            </>
          )}
        </div>

        {/* Hamburger */}
        <button
          className={`nav-hamburger${menuOpen ? " open" : ""}`}
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Toggle navigation"
        >
          <span />
          <span />
          <span />
        </button>

        {/* Mobile drawer */}
        <div className={`nav-drawer${menuOpen ? " open" : ""}`}>
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `nav-drawer-link${isActive ? " active" : ""}`
            }
            onClick={closeAll}
          >
            Home
          </NavLink>
          <NavLink
            to="/jobs"
            className={({ isActive }) =>
              `nav-drawer-link${isActive ? " active" : ""}`
            }
            onClick={closeAll}
          >
            Careers
          </NavLink>
          <NavLink
            to="/training"
            className={({ isActive }) =>
              `nav-drawer-link${isActive ? " active" : ""}`
            }
            onClick={closeAll}
          >
            Training
          </NavLink>

          {/* ✅ NEW: Discord link in mobile drawer */}
          <NavLink
            to="/discord"
            className={({ isActive }) =>
              `nav-drawer-link${isActive ? " active" : ""}`
            }
            onClick={closeAll}
          >
            Discord
          </NavLink>

          {currentUser && (
            <NavLink
              to={dashboardPath}
              className={({ isActive }) =>
                `nav-drawer-link${isActive ? " active" : ""}`
              }
              onClick={closeAll}
            >
              Dashboard
            </NavLink>
          )}
          <div className="nav-drawer-section">Products</div>
          {PRODUCTS.map((p, i) => (
            <a
              key={i}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className="nav-drawer-product-link"
              onClick={closeAll}
            >
              <img src={p.logo} alt={p.name} width="28" height="28" />
              <span className="nav-drawer-product-name">{p.name}</span>
            </a>
          ))}
          <div className="nav-drawer-footer">
            {!currentUser ? (
              <>
                <button
                  className="btn btn-outline"
                  style={{ width: "100%", justifyContent: "center" }}
                  onClick={() => {
                    setShowAuth("login");
                    closeAll();
                  }}
                >
                  Sign In
                </button>
                <button
                  className="btn btn-primary"
                  style={{ width: "100%", justifyContent: "center" }}
                  onClick={() => {
                    setShowAuth("register");
                    closeAll();
                  }}
                >
                  Get Started
                </button>
              </>
            ) : (
              <>
                {showChat && (
                  <div style={{ marginBottom: "0.5rem" }} onClick={closeAll}>
                    <ChatWidget user={currentUser} asNavButton={true} />
                  </div>
                )}
                <button
                  className="btn btn-ghost"
                  style={{ width: "100%", justifyContent: "center" }}
                  onClick={() => {
                    logout();
                    closeAll();
                  }}
                >
                  Sign Out
                </button>
              </>
            )}
          </div>
        </div>
        {menuOpen && <div className="nav-backdrop" onClick={closeAll} />}
      </nav>
    </>
  );
}
